import React, {
  FC,
  useEffect,
  useCallback,
  useState,
  useMemo,
  useRef,
} from 'react';
import { Project as ProjectModel } from '@material-bank/projects-api';
import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useValidateProjectData,
  useCreateProjectButtonAnalyticsIdentifier,
  useProjectsProvider,
} from '@material-bank/projects-utils';
import { useSwitchCurrentUserProject } from '@mb/api';
import {
  getUserAttributes,
  CustomEventName,
  AnalyticsEventLocation,
  AnalyticsEventName,
  Project,
} from '@mb/lib';
import { setSnackbarInfo, SnackbarStatus } from '@mb/redux/slices';
import Box from '@mui/system/Box';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useIsMobileDevice } from 'utils';

import * as Styled from './ProjectSelectionDialog.styles';

import { ProjectsListDialogView } from './ProjectsListDialogView';
import { definedTranslation } from '../../i18n';

export enum ProjectSelectionDialogView {
  SET_PROJECT = 'SET_PROJECT',
}

export const ProjectSelectionDialog: FC = () => {
  const { activeProjects, projectsError } = useProjectsProvider();
  const { isProjectDataComplete } = useValidateProjectData();
  const { analyticsProps } = useCreateProjectButtonAnalyticsIdentifier();

  const [open, setOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    null,
  );
  const [applyButtonDisabled, setApplyButtonDisabled] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState<number | null>(null);

  const dispatch = useDispatch();

  const onCurrentProjectSaveRef = useRef<
    ((project: ProjectModel | Project) => void) | null
  >(null);
  const onProjectCreateRef = useRef<((project: ProjectModel) => void) | null>(
    null,
  );
  const analyticsEventLocationRef = useRef<AnalyticsEventLocation>(
    AnalyticsEventLocation.SET_PROJECT_MODAL,
  );
  const isMobileDevice = useIsMobileDevice('md');
  const intl = useIntl();

  const labels = {
    createProject: intl?.formatMessage(definedTranslation.createProjectButton),
    save: intl?.formatMessage(definedTranslation.saveButton),
    itemNotAddedToCart: intl?.formatMessage(
      definedTranslation.itemNotAddedToCartMessage,
    ),
  };

  const sortedProjects = useMemo(() => {
    const projectsData = Object.values(activeProjects) || [];

    // current project should be on the first position
    return projectsData
      .slice()
      .sort((projectA, projectB) =>
        projectA.externalId === currentProjectId
          ? -1
          : projectB.externalId === currentProjectId
          ? 1
          : 0,
      );
  }, [activeProjects, currentProjectId]);

  const { isLoading: isSetProjectLoading, setCurrentUserProject } =
    useSwitchCurrentUserProject({
      onSuccess: (project) => {
        onCurrentProjectSaveRef.current?.(project);
      },
    });

  useEffect(() => {
    const projectsData = Object.values(activeProjects) || [];

    if (projectsData.length && !selectedProjectId) {
      setSelectedProjectId(projectsData[0].externalId);
    }
  }, [activeProjects, selectedProjectId]);

  const onCreateProjectSuccessWithoutModalClose = (project: ProjectModel) => {
    setCurrentUserProject(project, false);
    onProjectCreateRef.current?.(project);

    mitGTM.sendEvent({
      event: AnalyticsEventName.CREATE_PROJECT,
      event_location: AnalyticsEventLocation.SET_PROJECT_MODAL,
      action: null,
      type: null,
      name: null,
      method: null,
      list_id: null,
      list_name: null,
      experiment_name: null, // null unless event is part of split test, if so feature flag name
      experiment_version: null, // null unless event is part of split test, if so treatment name
      project: {
        project_type: project.type?.label,
        project_phase: project.phase?.label,
      },
    });
  };

  const handleOpen = useCallback(
    (
      event: CustomEvent<{
        currentProjectId: number;
        onCurrentProjectSave: (project: ProjectModel) => void;
        onProjectCreate: (project: ProjectModel) => void;
        analyticsEventLocation: AnalyticsEventLocation;
        defaultView: ProjectSelectionDialogView;
      }>,
    ) => {
      const { detail } = event;
      const {
        currentProjectId,
        onCurrentProjectSave,
        onProjectCreate,
        analyticsEventLocation,
      } = detail ?? {};

      if (currentProjectId) {
        setCurrentProjectId(Number(currentProjectId));
        setSelectedProjectId(Number(currentProjectId));
      } else {
        const userData = getUserAttributes();
        const parsedUserCurrentProjectId = Number(userData?.current_project);
        const userCurrentProjectId = Number.isFinite(parsedUserCurrentProjectId)
          ? parsedUserCurrentProjectId
          : null;

        setCurrentProjectId(userCurrentProjectId);
        setSelectedProjectId(userCurrentProjectId);
      }

      mitGTM.sendEvent({
        event: AnalyticsEventName.SET_PROJECT,
        event_location: analyticsEventLocation ?? null,
        action: 'View',
      });

      onCurrentProjectSaveRef.current = onCurrentProjectSave;
      onProjectCreateRef.current = onProjectCreate;

      if (analyticsEventLocation) {
        analyticsEventLocationRef.current = analyticsEventLocation;
      }

      setOpen(true);
    },
    [],
  );

  const onClose = (
    _event: React.SyntheticEvent | Event,
    reason?: 'backdropClick' | 'escapeKeyDown',
  ) => {
    closeHandler(reason);
  };

  const closeHandler = (reason?: 'backdropClick' | 'escapeKeyDown') => {
    setOpen(false);
    setApplyButtonDisabled(false);

    if (reason === 'backdropClick') {
      mitGTM.sendEvent({
        event: AnalyticsEventName.SET_PROJECT,
        event_location: analyticsEventLocationRef.current,
        action: 'Close',
      });

      dispatch(
        setSnackbarInfo({
          isOpen: true,
          message: labels.itemNotAddedToCart,
          status: SnackbarStatus.DEFAULT,
        }),
      );
    }
  };

  const onProjectButtonClick = (project: ProjectModel) => {
    setSelectedProjectId(project.externalId);

    mitGTM.sendEvent({
      event: AnalyticsEventName.SET_PROJECT,
      event_location: AnalyticsEventLocation.SET_PROJECT_MODAL,
      action: 'Select',
    });
  };

  const onSaveButtonClick = () => {
    const selectedProject = selectedProjectId
      ? activeProjects[selectedProjectId]
      : null;

    if (!selectedProject) {
      return;
    }

    if (!isProjectDataComplete(selectedProject)) {
      projectsEditDialogConfig.openProjectEditDialog({
        projectId: selectedProject.externalId,
        successCallback(project: ProjectModel) {
          setCurrentUserProject(project);
          mitGTM.sendEvent({
            event: AnalyticsEventName.SET_PROJECT,
            event_location: AnalyticsEventLocation.SET_PROJECT_MODAL,
            action:
              selectedProject.externalId === currentProjectId
                ? 'Confirm'
                : 'Change',
          });
          closeHandler();
        },
      });

      return;
    }

    setCurrentUserProject(selectedProject);
    mitGTM.sendEvent({
      event: AnalyticsEventName.SET_PROJECT,
      event_location: AnalyticsEventLocation.SET_PROJECT_MODAL,
      action:
        selectedProject.externalId === currentProjectId ? 'Confirm' : 'Change',
    });

    closeHandler();
  };

  const onLeftButtonClick = () => {
    mitGTM.sendEvent({
      event: AnalyticsEventName.SET_PROJECT,
      event_location: AnalyticsEventLocation.SET_PROJECT_MODAL,
      action: 'Create',
    });

    window.dispatchEvent(
      new CustomEvent('open-create-project-modal', {
        detail: {
          showModalAfterCreate: false,
          eventLocation: AnalyticsEventLocation.SET_PROJECT_MODAL,
          successCallback: (project: ProjectModel) => {
            onCreateProjectSuccessWithoutModalClose(project);
          },
          closeCallback: () => {
            closeHandler();
          },
        },
      }),
    );
  };

  useEffect(() => {
    window.addEventListener(
      CustomEventName.OPEN_PROJECT_SELECTION_DIALOG,
      handleOpen,
    );

    return () => {
      window.removeEventListener(
        CustomEventName.OPEN_PROJECT_SELECTION_DIALOG,
        handleOpen,
      );
    };
  }, [handleOpen]);

  if (open && !currentProjectId) {
    window.dispatchEvent(
      new CustomEvent(CustomEventName.OPEN_CREATE_PROJECT_MODAL, {
        detail: {
          eventLocation: AnalyticsEventLocation.SET_PROJECT_MODAL,
        },
      }),
    );

    setOpen(false);
    return null;
  }
  return (
    <Styled.Dialog
      open={open}
      onClose={onClose}
      sx={{
        zIndex: 39,
      }}
      title={intl?.formatMessage(definedTranslation.title)}
      disableEnforceFocus
      goBackLabel=""
      leftButtonLabel={labels.createProject}
      onLeftButtonClick={onLeftButtonClick}
      leftButtonVariant="contained"
      leftButtonDense={false}
      leftButtonColor="secondary"
      dialogTitleSx={Styled.dialogTitle}
      leftButtonSx={Styled.leftButton}
      applyButtonSx={Styled.applyButton}
      cancelButtonSx={Styled.cancelButton}
      onApplyButtonClick={onSaveButtonClick}
      applyButtonLabel={labels.save}
      isApplyLoading={isSetProjectLoading}
      applyButtonDisabled={applyButtonDisabled}
      mobileFullscreen
      showCloseIcon={isMobileDevice}
      leftButtonProps={analyticsProps}
    >
      <Box sx={Styled.contentContainer}>
        {!projectsError ? (
          <ProjectsListDialogView
            selectedProjectId={selectedProjectId}
            projects={sortedProjects}
            onProjectButtonClick={onProjectButtonClick}
            intl={intl}
          />
        ) : (
          <div>There was an error while loading projects.</div>
        )}
      </Box>
    </Styled.Dialog>
  );
};
